import { BodyR, Button, HeadingRegular, Link } from '@pensionbee/honeypot-ui';
import { StageProps } from '../../config/StrongerNudgeConfig';
import {
  WarningDataQuestion,
  warningData,
  WarningDataSchema,
} from './RiskQuestionData';
import { useRegisterStrongerNudgeBackOverride } from '@/domains/stronger-nudge/context/StrongerNudgeBackOverrideContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RiskAnswers } from './RiskQuestionData';
import { Controller, useForm } from 'react-hook-form';
import { RadioGroup } from '@/domains/shared/components/radioGroup/radioGroup';
import { Switch } from '@/domains/shared/components/switch/Switch';
import CustomError from '@/pages/_error';

export const RiskWarningsPartial = ({
  onSubmit,
}: {
  onSubmit: StageProps['onSubmit'];
}) => {
  const defaultRiskAnswers: Record<WarningDataQuestion, RiskAnswers> =
    Object.fromEntries(
      Object.keys(warningData).map((key) => [key, '' as RiskAnswers]),
    ) as Record<WarningDataQuestion, RiskAnswers>;

  const riskAnswersRef =
    useRef<Record<WarningDataQuestion, RiskAnswers>>(defaultRiskAnswers);

  function findQuestionFromPosition(position: number): WarningDataQuestion {
    const questionKey = Object.keys(warningData).find(
      (key) => warningData[key as WarningDataQuestion].position === position,
    );
    return questionKey as WarningDataQuestion;
  }

  const [currentRiskQuestion, setCurrentRiskQuestion] =
    useState<WarningDataQuestion>(findQuestionFromPosition(1));
  const [askRiskQuestions, setAskRiskQuestions] = useState(false);

  type FormValues = {
    riskAnswer: RiskAnswers;
    customerUnderstands: boolean;
  };

  const { control, watch, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      riskAnswer: '',
      customerUnderstands: false,
    },
    shouldUnregister: true,
  });

  const riskAnswer = watch('riskAnswer');
  const customerUnderstands = watch('customerUnderstands');

  function updateCurrentRiskQuestion() {
    const positionOfNextQuestion =
      warningData[currentRiskQuestion].position + 1;
    if (positionOfNextQuestion <= Object.keys(warningData).length) {
      const nextQuestion = findQuestionFromPosition(positionOfNextQuestion);
      setCurrentRiskQuestion(nextQuestion);
    }
  }

  const handleHeaderBack = useCallback(() => {
    if (!askRiskQuestions) {
      return false;
    }

    const positionOfPreviousQuestion =
      warningData[currentRiskQuestion].position - 1;

    if (positionOfPreviousQuestion >= 1) {
      setCurrentRiskQuestion(
        findQuestionFromPosition(positionOfPreviousQuestion),
      );
      return true;
    }

    setAskRiskQuestions(false);
    return true;
  }, [askRiskQuestions, currentRiskQuestion]);

  useRegisterStrongerNudgeBackOverride(handleHeaderBack, [handleHeaderBack]);

  function handleRiskAnswer(data: FormValues) {
    const newRiskAnswers = {
      ...riskAnswersRef.current,
      [currentRiskQuestion]: data.riskAnswer,
    };
    riskAnswersRef.current = newRiskAnswers;

    const allAnswered = Object.values(newRiskAnswers).every(
      (answer) => answer !== '',
    );
    if (allAnswered) {
      void onSubmit(newRiskAnswers);
    } else {
      updateCurrentRiskQuestion();
    }
  }

  useEffect(() => {
    reset({ riskAnswer: '', customerUnderstands: false });
  }, [currentRiskQuestion, reset]);

  const currentData = warningData[currentRiskQuestion];
  const warningVisible = riskAnswer === currentData.answerRequiringWarning;
  const buttonDisabled = !(
    riskAnswer === currentData.answerNotRequiringWarning ||
    (warningVisible && customerUnderstands)
  );

  const validationResult = WarningDataSchema.safeParse(warningData);
  if (!validationResult.success) {
    console.log('Warning Data is malformed: ' + validationResult.error);
    return <CustomError statusCode={500} />;
  }

  return (
    <div>
      {!askRiskQuestions && (
        <div data-testid="risk-warnings-intro-page">
          <div className="mb-28">
            <HeadingRegular data-testid="risk-warnings-intro-header">
              Before you proceed, we need to ask you some further questions.
            </HeadingRegular>
          </div>
          <div className="mb-28">
            <BodyR
              className="mb-28"
              data-testid="risk-warnings-intro-description"
            >
              The law requires us to ask these questions, as we need to confirm
              that you understand the implications of your financial decisions.
              If you need help please{' '}
              <Link
                href={`${process.env.NEXT_PUBLIC_API_URL}/uk/contact-us`}
                target="_blank"
              >
                get in touch
              </Link>
              .
            </BodyR>
          </div>
          <Button
            className="w-full"
            data-testid="risk-warnings-intro-next-button"
            onClick={() => setAskRiskQuestions(true)}
          >
            Next
          </Button>
        </div>
      )}
      {askRiskQuestions && (
        <div data-testid="risk-warnings-question-page">
          <div className="mb-28">
            <HeadingRegular data-testid="risk-warning-question-header">
              {currentData.questionCopy}
            </HeadingRegular>
          </div>
          <form onSubmit={handleSubmit(handleRiskAnswer)}>
            <div className="mb-28">
              <Controller
                control={control}
                name="riskAnswer"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    orientation="horizontal"
                    onChange={field.onChange}
                    label={currentData.questionCopy}
                    options={[
                      { label: 'Yes', value: 'Yes' },
                      { label: 'No', value: 'No' },
                    ]}
                  />
                )}
              />
            </div>
            {warningVisible && (
              <div className="mb-28" data-testid="risk-warning">
                <div className="mb-28">
                  <BodyR data-testid="risk-warning-text">
                    {currentData.warning}
                  </BodyR>
                </div>
                <Controller
                  control={control}
                  name="customerUnderstands"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      label="I understand"
                      className="mb-28"
                    />
                  )}
                />
              </div>
            )}
            <Button className="w-full" type="submit" disabled={buttonDisabled}>
              Next
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};
